import { getUserBehavioralDiscounts } from 'actions/v3/userBehavioralDiscounts';
import { checkOnboardingAndDismissIfStale } from 'actions/dismissals';
import { getFavorites } from 'actions/favorites';
import { getRecommendedMealsForBasket } from 'actions/recommendedMeals';
import { getStreakOffers } from 'actions/streakOffers';
import { getSuggestedPairings } from 'actions/suggestedPairings';
import { getViewableBaskets, getBasket } from 'actions/v3/baskets';
import { getMenuCategories } from 'actions/v3/menuCategories';
import { getFullMenuMealsData } from 'actions/v3/menuMeals';
import { getMenu } from 'actions/v3/menus';
import { getQuickFilters } from 'actions/v3/quickFilters';
import { getCompletedReviews } from 'actions/v3/reviews';
import { getUser } from 'actions/v3/user';
import {
  BOOTSTRAP_DASHBOARD_V3,
  BOOTSTRAP_DASHBOARD_V3_SUCCESS,
  BOOTSTRAP_DASHBOARD_V3_ERROR,
  MENU_REFRESH_LOADING,
  MENU_REFRESH_COMPLETE,
} from 'lib/constants/v3Actions';
import * as fromAccount from 'reducers/account';
import * as fromCookbook from 'reducers/cookbook';
import * as fromDashboard from 'reducers/dashboard';
import * as fromV3Dashboard from 'reducers/v3/dashboard';

const bootstrapDashboard = menuSlug => async (dispatch, getState) => {
  const state = getState();

  dispatch({ type: BOOTSTRAP_DASHBOARD_V3 });

  try {
    const [userResponse] = await Promise.all([
      dispatch(getUser()),
      fromV3Dashboard.needsInitializing(state) && dispatch(getViewableBaskets()),
    ]);

    if (userResponse.ok) {
      dispatch(checkOnboardingAndDismissIfStale(userResponse.user));
    }

    await dispatch(fetchCompleteBasket(menuSlug));

    dispatch({ type: BOOTSTRAP_DASHBOARD_V3_SUCCESS });
  } catch {
    dispatch({ type: BOOTSTRAP_DASHBOARD_V3_ERROR });
  }
};

export const ensureCompletedReviewsData = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const completedReviewsFetched = fromV3Dashboard.selectCompletedReviewsFetched(state);
    const completedReviewsLoading = fromV3Dashboard.selectCompletedReviewsLoading(state);

    if (!completedReviewsFetched && !completedReviewsLoading) {
      await dispatch(getCompletedReviews());
    }
  };
};

export const ensureFavoritesData = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const favoritesFetched = fromCookbook.selectFavoritesFetched(state);
    const favoritesLoading = fromCookbook.selectFavoritesLoading(state);

    if (!favoritesFetched && !favoritesLoading) {
      await dispatch(getFavorites());
    }
  };
};

export const fetchCompleteBasket = menuSlug => {
  return (dispatch, getState) => {
    const state = getState();
    const shouldFetchMenu = fromV3Dashboard.menuNeedsInitialization(state, menuSlug);
    const shouldFetchBasket = fromV3Dashboard.basketNeedsInitialization(state, menuSlug);
    const hasSuggestedPairings =
      fromDashboard.selectSuggestedPairingsForMenu(state, menuSlug).length > 0;
    const hasBehavioralDiscounts =
      fromV3Dashboard.selectUserBehavioralDiscountsForMenu(state, menuSlug).length > 0;
    const hasStreakOffers = fromAccount.selectStreakOffers(state).length > 0;
    const basketSlug = fromV3Dashboard.selectBasket(state, menuSlug).slug;
    const promises = [
      shouldFetchBasket && dispatch(getBasket(basketSlug, menuSlug)),
      shouldFetchMenu && dispatch(getMenu(menuSlug)),
      !hasSuggestedPairings && dispatch(getSuggestedPairings(menuSlug)),
      !hasBehavioralDiscounts && dispatch(getUserBehavioralDiscounts(menuSlug)),
      !hasStreakOffers && dispatch(getStreakOffers()),
      dispatch(ensureCompletedReviewsData()),
      dispatch(ensureFavoritesData()),
    ].filter(Boolean);
    return Promise.all(promises);
  };
};

export const bootstrapEditBasket = (menuSlug, mealPlanSlug, isEasyPrep) => async dispatch => {
  await dispatch(bootstrapDashboard(menuSlug));

  return Promise.all([
    dispatch(getFullMenuMealsData(menuSlug, mealPlanSlug)),
    dispatch(getMenuCategories(menuSlug, mealPlanSlug, isEasyPrep)),
    dispatch(getQuickFilters(menuSlug, mealPlanSlug)),
  ]);
};

export const refreshMenuFromDeliveryDayChange =
  (menuSlug, fetchRecommendedMeals = false) =>
  async (dispatch, getState) => {
    dispatch({ type: MENU_REFRESH_LOADING, menuSlug });

    const state = getState();
    const basket = fromV3Dashboard.selectBasket(state, menuSlug);
    const { mealPlan } = basket;

    await dispatch(getMenu(menuSlug));
    await dispatch(getFullMenuMealsData(menuSlug, mealPlan));

    if (fetchRecommendedMeals) {
      await dispatch(getRecommendedMealsForBasket(menuSlug));
    }

    dispatch({ type: MENU_REFRESH_COMPLETE, menuSlug });
  };
